<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center gap-1.5 py-10">
      <span class="dot"></span><span class="dot"></span><span class="dot"></span>
    </div>

    <!-- Empty -->
    <div v-else-if="!reports.length" class="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400 text-xs py-10 px-4 text-center">
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      {{ $t("ai.widget.report.noReports") }}
    </div>

    <!-- List -->
    <div v-else class="flex-1 overflow-y-auto px-3 py-3 space-y-2">
      <div v-for="report in reports" :key="report.uuid" class="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
        <!-- Title row -->
        <div class="flex items-start justify-between gap-2">
          <p class="text-xs font-semibold text-gray-800 m-0 leading-snug flex-1 truncate">{{ report.title }}</p>
          <div class="flex items-center gap-1.5 flex-shrink-0">
            <span
              :class="report.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-500 border-gray-200'"
              class="inline-flex items-center px-1.5 py-0.5 rounded-full border text-xs font-semibold leading-none"
            >
              {{ report.active ? $t("ai.widget.report.active") : $t("ai.widget.report.inactive") }}
            </span>
            <button
              v-if="deleteConfirm !== report.uuid"
              @click="$emit('update:deleteConfirm', report.uuid)"
              class="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer border-0 bg-transparent"
              :title="$t('ai.widget.report.delete')"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
            </button>
            <template v-else>
              <button @click="$emit('delete', report.uuid)" class="px-2 py-0.5 rounded text-xs font-semibold bg-red-500 text-white border-0 cursor-pointer hover:bg-red-600 transition-colors">
                {{ $t("ai.widget.report.confirmDelete") }}
              </button>
              <button @click="$emit('update:deleteConfirm', null)" class="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors">
                {{ $t("ai.widget.report.cancel") }}
              </button>
            </template>
          </div>
        </div>

        <!-- Meta -->
        <div class="mt-2 space-y-1">
          <div class="flex items-center gap-1.5 text-xs text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <span class="truncate">{{ report.email }}</span>
          </div>
          <div class="flex items-center gap-1.5 text-xs text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>{{ formatSchedule(report) }}</span>
          </div>
          <div v-if="report.next_send_at" class="flex items-center gap-1.5 text-xs text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>{{ $t("ai.widget.report.nextSend") }}: {{ formatDate(report.next_send_at) }}</span>
          </div>
          <div v-if="report.last_sent_at" class="flex items-center gap-1.5 text-xs text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            <span>{{ $t("ai.widget.report.lastSent") }}: {{ formatDate(report.last_sent_at) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Refresh -->
    <div class="flex-shrink-0 px-3 py-2 border-t border-gray-100 flex justify-end">
      <button
        @click="$emit('refresh')"
        :disabled="loading"
        class="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 cursor-pointer bg-transparent border-0 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
        {{ $t("ai.widget.report.refresh") }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "AiReportsTab",

  props: {
    reports:       { type: Array,  default: () => [] },
    loading:       { type: Boolean, default: false },
    deleteConfirm: { type: String,  default: null },
    language:      { type: String,  default: "en" },
  },

  emits: ["refresh", "delete", "update:deleteConfirm"],

  methods: {
    formatSchedule(report) {
      const typeMap = {
        none:    this.$t("ai.widget.report.scheduleNone"),
        daily:   this.$t("daily"),
        weekly:  this.$t("weekly"),
        monthly: this.$t("monthly"),
      };
      const label = typeMap[report.schedule_type] || report.schedule_type;
      if (report.schedule_type === "none") return label;
      const time = report.schedule_time ? ` · ${report.schedule_time}` : "";
      return `${label}${time}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString(
        this.language === "fr" ? "fr-FR" : "en-US",
        { day: "2-digit", month: "short", year: "numeric" },
      );
    },
  },
};
</script>

<style scoped>
.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: ai-bounce 1.4s infinite both;
}
.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }
@keyframes ai-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
  40%            { transform: scale(1); opacity: 1; }
}
</style>
